import { NgClass } from "@angular/common";
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService } from "primeng/dynamicdialog";
import { PanelModule } from "primeng/panel";
import { ToastModule } from "primeng/toast";
import { filter } from "rxjs";
import { BreadcrumbComponent } from "./components/breadcrumb/breadcrumb.component";
import { NavComponent } from "./components/nav/nav.component";
import { DimensionsSyncService } from "./services/dimensions-sync.service";
import { UserService } from "./services/user.service";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    RouterModule,
    ButtonModule,
    NavComponent,
    BreadcrumbComponent,
    PanelModule,
    NgClass,
    ToastModule,
    ConfirmDialogModule,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  providers: [MessageService, ConfirmationService, DialogService],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  @ViewChild("maxWidthEl") maxWidthEl!: ElementRef;
  @ViewChild("appNav") appNav!: ElementRef;
  private resizeObserverFull!: ResizeObserver;
  private resizeObserverNav!: ResizeObserver;
  isInAuthModule: WritableSignal<boolean> = signal(false);
  needsObserverInit: WritableSignal<boolean> = signal(false);

  constructor(
    public userService: UserService,
    private router: Router,
    private dimensionsSyncService: DimensionsSyncService,
    private destroyRef: DestroyRef
  ) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.isInAuthModule.set(event.url.startsWith("/auth"));
      });

    toObservable(this.userService.isAuthenticated)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((isAuthenticated) => !!isAuthenticated)
      )
      .subscribe(() => {
        this.needsObserverInit.set(true);
        this.checkAndInitializeObservers();
      });
  }

  ngOnDestroy(): void {
    this.resizeObserverNav?.disconnect();
    this.resizeObserverFull?.disconnect();
  }

  ngAfterViewInit(): void {
    this.checkAndInitializeObservers();
  }

  private checkAndInitializeObservers(attempt = 0) {
    if (
      this.needsObserverInit() &&
      (!this.maxWidthEl?.nativeElement || !this.appNav?.nativeElement)
    ) {
      if (attempt < 10) {
        setTimeout(() => this.checkAndInitializeObservers(attempt + 1), 50);
      }
      return;
    }
    if (
      this.needsObserverInit() &&
      this.maxWidthEl?.nativeElement &&
      this.appNav?.nativeElement
    ) {
      this.needsObserverInit.set(false);
      this.initializeResizeObservers();
    }
  }

  private initializeResizeObservers(): void {
    const fullWidth = this.maxWidthEl.nativeElement;
    const navElement = this.appNav.nativeElement;

    this.resizeObserverFull = new ResizeObserver(() => {
      this.dimensionsSyncService.setWidth(
        fullWidth.offsetWidth - navElement.offsetWidth
      );
    });

    this.resizeObserverFull.observe(fullWidth);

    this.resizeObserverNav = new ResizeObserver(() => {
      this.dimensionsSyncService.setWidth(
        fullWidth.offsetWidth - navElement.offsetWidth
      );
    });

    this.resizeObserverNav.observe(navElement);
  }
}
